import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { useMutation } from "@grandvisionhq/graphql";
import { useCartStorage } from "@grandvisionhq/lib-utils-cart";
import { useDispatchTrackErrorEvent } from "@grandvisionhq/svc-tealium/events";
import { errorsMapping } from "../../../../mappers/mappers/errors-mapping.js";
import { useMutationConfig } from "./use-mutation-config.js";
import { useUpdateCartCache } from "./use-update-cart-cache.js";
import { getFlattenedCartFailureErrorCodes } from "../../../../utils/utils/get-flattened-cart-error-codes.js";
import { SET_SERVICE_STORE } from "../../../../operations/mutations/operations/mutations/set-service-store.js";
const useSetServiceStore = ({ onError, onCompleted } = {}) => {
  const [cart] = useCartStorage();
  const { updateCacheWithCart } = useUpdateCartCache();
  const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
  const handleOnCompleted = React.useCallback(
    (responseData) => {
      let result;
      if ("setServiceStore" in responseData) {
        result = responseData.setServiceStore;
        if (result.failures?.length) {
          const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
          onError?.({ reason: "MUTATION" });
          errorCodes.forEach((error) => toast.failed(error));
        }
        if (result.success) {
          onCompleted?.("MUTATION");
        }
      }
    },
    [onCompleted, onError]
  );
  const handleOnError = React.useCallback(() => {
    onError?.({ reason: "MUTATION" });
    dispatchTrackErrorEvent(
      () => Promise.resolve({
        Error_Source: "Server",
        Error_Code: "Set service store error",
        Error_Details: "Mutation failed",
        Error_Message: errorsMapping.SOMETHING_WENT_WRONG
      })
    );
    toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
  }, [dispatchTrackErrorEvent, onError]);
  const { mutationOptions } = useMutationConfig({
    onError: handleOnError,
    onCompleted: handleOnCompleted
  });
  const updateCache = React.useCallback(
    (cache, { data }) => {
      if (data && data.setServiceStore.success) {
        const updatedCart = data.setServiceStore.cart;
        updatedCart && updateCacheWithCart({ cart: updatedCart, cache });
      }
    },
    [updateCacheWithCart]
  );
  const [mutationCallback, mutation] = useMutation(SET_SERVICE_STORE, {
    ...mutationOptions,
    // reason: typescript complains about a typing mismatch for the data prop
    update: updateCache,
    context: { forwardHeaders: { "cart-hash": cart.cartHash } }
  });
  const setServiceStore = React.useCallback(
    async (globalStoreId) => {
      if (!cart.cartId) {
        console.error("Cart ID is missing");
        return;
      }
      await mutationCallback({
        variables: {
          storeInput: {
            globalStoreId,
            checkoutId: cart.cartId
          }
        }
      });
    },
    [mutationCallback, cart.cartId]
  );
  return {
    setServiceStore,
    isUpdating: mutation.loading,
    updateError: mutation.error,
    called: mutation.called
  };
};
export {
  useSetServiceStore
};
//# sourceMappingURL=use-set-service-store.js.map
